import {signal, computed} from '@preact/signals-core';
import {userSignal} from '../auth/auth.store.js';
import {morphedRead} from './morphed-entity.service.js';

/** Asignación de un tool a un rol con flags CRUD. El toolKey por convención
 *  coincide con el entityEndpoint de la entidad (para herramientas-entidad).
 *  Para herramientas no-entidad (scanner, people-loader, etc.) solo se usa
 *  canView (los flags CRUD se ignoran en la UI de entidad). */
export interface RoleTool {
    toolKey: string;
    canView: boolean;
    canCreate: boolean;
    canEdit: boolean;
    canDelete: boolean;
}

export interface ClamRole {
    id: string;
    key: string;
    name: string;
    description?: string;
    tools?: RoleTool[];
    users?: string[];
    isSystem?: boolean;
    deleted?: boolean;
}

/** Todos los roles clam definidos (CRUD desde el admin). */
export const rolesSignal = signal<ClamRole[]>([]);
export const permissionsLoaded = signal(false);
export const permissionsLoading = signal(false);
export const permissionsError = signal('');

/** Roles del usuario logueado (cuyo Cognito sub esta en role.users). */
export const myRoles = computed<ClamRole[]>(() => {
    const sub = userSignal.value?.userId ?? '';
    if (!sub) return [];
    return rolesSignal.value.filter((r) => (r.users || []).includes(sub));
});

/** ¿Es admin clam? Tiene un rol con key 'admin' (o isSystem). El admin clam se
 *  espera que ademas sea morphed-admin (requerido por /api/auth/* en backend). */
export const isClamAdmin = computed<boolean>(() =>
    myRoles.value.some((r) => r.key === 'admin' || r.isSystem === true),
);

/** Normaliza un RoleTool crudo (de la API) garantizando los 4 flags CRUD.
 *  Roles legacy guardados como {toolKey, canView:true} se completan con
 *  canCreate/canEdit/canDelete = false (向后兼容). */
function normalizeRoleTool(t: Partial<RoleTool>): RoleTool {
    return {
        toolKey: t.toolKey || '',
        canView: !!t.canView,
        canCreate: !!t.canCreate,
        canEdit: !!t.canEdit,
        canDelete: !!t.canDelete,
    };
}

/** Mapa toolKey → flags CRUD agregados (OR) sobre todos los roles del usuario.
 *  Si dos roles asignan el mismo tool, el usuario tiene la unión de flags. */
const myToolFlags = computed<Map<string, RoleTool>>(() => {
    const map = new Map<string, RoleTool>();
    for (const r of myRoles.value) {
        for (const raw of r.tools || []) {
            const t = normalizeRoleTool(raw);
            if (!t.toolKey) continue;
            const ex = map.get(t.toolKey);
            if (!ex) { map.set(t.toolKey, t); continue; }
            ex.canView = ex.canView || t.canView;
            ex.canCreate = ex.canCreate || t.canCreate;
            ex.canEdit = ex.canEdit || t.canEdit;
            ex.canDelete = ex.canDelete || t.canDelete;
        }
    }
    return map;
});

/** ¿El usuario actual tiene el tool asignado (en cualquier rol), aunque
 *  sea sin permiso de lectura? Se usa para mostrar el item del menú pero
 *  renderizar la vista como "sin permiso de lectura" si canView=false. */
export function hasTool(toolKey: string): boolean {
    return myToolFlags.value.has(toolKey);
}

/** ¿El usuario actual puede ver (tiene acceso a) el tool en el side nav?
 *  Estricto por tools asignados a sus roles (sin bypass de admin).
 *  Las herramientas Morphed se controlan aparte via canAccessMorphed. */
export function canView(toolKey: string): boolean {
    const t = myToolFlags.value.get(toolKey);
    return !!t && t.canView;
}

/** Flags CRUD por tool (para gating de botones en entity-manager). */
export function canCreate(toolKey: string): boolean {
    const t = myToolFlags.value.get(toolKey);
    return !!t && t.canCreate;
}
export function canEdit(toolKey: string): boolean {
    const t = myToolFlags.value.get(toolKey);
    return !!t && t.canEdit;
}
export function canDelete(toolKey: string): boolean {
    const t = myToolFlags.value.get(toolKey);
    return !!t && t.canDelete;
}

/** ¿Tiene el usuario algún tool asignado (para estado "sin acceso")? */
export const hasAnyTool = computed<boolean>(() => myToolFlags.value.size > 0);

/** Carga los roles clam desde morphed y marca permisos cargados. Idempotente. */
export async function loadPermissions(force = false): Promise<void> {
    if (permissionsLoaded.value && !force) return;
    if (permissionsLoading.value) return;
    permissionsLoading.value = true;
    permissionsError.value = '';
    try {
        const roles = await morphedRead<ClamRole>('clam-rol', {
            filters: [],
            options: {limit: 200},
        });
        rolesSignal.value = (roles || []).filter((r) => !r.deleted);
        permissionsLoaded.value = true;
    } catch (e) {
        permissionsError.value = String(e);
        rolesSignal.value = [];
        permissionsLoaded.value = true; // no bloquear la UI si falla
    } finally {
        permissionsLoading.value = false;
    }
}

export function resetPermissions(): void {
    rolesSignal.value = [];
    permissionsLoaded.value = false;
    permissionsError.value = '';
    myProfile.value = null;
}

// === Perfil propio + acceso a la zona Morphed ===

export const myProfile = signal<{id?: string; email?: string; name?: string; organizations?: string[]} | null>(null);
export const morphedOrgId = signal('');

/** ¿El usuario logueado pertenece a la org 'morphed' (super-admin/dev)? Habilita la sección Morphed. */
export const canAccessMorphed = computed<boolean>(() => {
    const orgs = myProfile.value?.organizations || [];
    return morphedOrgId.value !== '' && orgs.includes(morphedOrgId.value);
});

/** Carga el morphed_user propio (por id=sub) y resuelve el id de la org 'morphed'. */
export async function loadMyProfile(force = false): Promise<void> {
    const sub = userSignal.value?.userId;
    if (!sub) { myProfile.value = null; return; }
    try {
        const me = await morphedRead<{id: string; organizations?: string[]}>('morphed-user', {
            filters: [{id: sub}], options: {limit: 1},
        });
        myProfile.value = me[0] || null;
    } catch { myProfile.value = null; }

    if (!morphedOrgId.value || force) {
        try {
            const orgs = await morphedRead<{id: string; name: string}>('morphed-organization', {
                filters: [{name: 'morphed'}], options: {limit: 1},
            });
            morphedOrgId.value = orgs[0]?.id || '';
        } catch { /* ignore */ }
    }
}
